import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import CornerNotice from '../components/notifications/CornerNotice';
import { useAuth } from '../contexts/AuthContext';
import { usePageConfig } from '../contexts/PageConfigContext';
import { GroupsBreadcrumbs } from '../components/GroupsListTable';
import GroupsRowList from '../components/groups/GroupsRowList';
import GroupsOrgGroupsPanel from '../components/groups/GroupsOrgGroupsPanel';
import GroupsMembersPanel from '../components/groups/GroupsMembersPanel';
import GroupsDerivedGuardiansPanel from '../components/groups/GroupsDerivedGuardiansPanel';
import GroupsAddMemberModal from '../components/groups/GroupsAddMemberModal';
import GroupsCreateFab from '../components/groups/GroupsCreateFab';
import GroupsAddOrganizationEmptyState from '../components/groups/GroupsAddOrganizationEmptyState';
import GroupsCreateEntityFormFields from '../components/groups/GroupsCreateEntityFormFields';
import GroupsRoleTemplateField from '../components/groups/GroupsRoleTemplateField';
import GroupsMembershipKindField from '../components/groups/GroupsMembershipKindField';
import GroupsTeamAddContextFields from '../components/groups/GroupsTeamAddContextFields';
import {
  applyGroupRoleTemplatePermissions,
  GROUP_ROLE_TEMPLATE_NONE
} from '../constants/groupRoleTemplates';
import ProfileFormSheet from '../components/modals/ProfileFormSheet';
import {
  formatOrganizationGroupPeriodDescription,
  getEmptyCreateEntityForm,
  parseBatchGroupNames,
  periodDefinitionFromTeamGp,
  periodDefinitionIdsOnTeam,
  buildGroupPeriodComboSuggests,
  resolveCreateGroupPeriodOptions,
  resolveNestedTeamPeriodFromAttach,
  resolveParentEnrollmentContext,
  suggestGroupDescription,
  suggestGroupPeriodTitle,
  isPermanentPeriod
} from '../constants/groupPeriods';
import { formatTeamGroupsPeriodOption } from '../components/groups/GroupsTeamAddContextFields';
import {
  MY_GROUPS_TITLE,
  canBrowseAllOrganizations,
  buildGroupsPageBreadcrumbs,
  canCreateInGroupContext,
  filterGroupsByNamePolicy,
  getGroupPageSubtitle,
  findOrganizationByOrgGpId,
  findOrganizationInList,
  mapGroupsPeriodsRowsToGroups,
  prependSystemGroupsToOrganizationList,
  filterNestedGroupsToUserAccess,
  canAddMembersToGroup,
  canOpenMemberProfilesInGroup,
  canViewGroupMembersInGroup,
  canViewMemberDetailsInGroup,
  canViewMemberAccessInGroup,
  getGroupsPageCapabilities,
  userHasSameLevelBrowseGrant,
  isSystemOrganizationContext,
  nonPlatformOwnerBlockedOnOwnerTeam,
  ownerTeamActionBlockedReason,
  ownerTeamActionBlockedMessage
} from '../utils/groupsPage';
import {
  getDerivedGuardiansParentGpId,
  isDerivedGuardiansGroup
} from '../constants/derivedGuardians';
import { resolveGroupIdForGpId } from '../utils/orgGroupTree';
import { bulkImportMembersInBatches, BULK_MEMBER_IMPORT_BATCH_SIZE } from '../utils/bulkMemberImport';
import './Groups.css';

const GroupsOrganizationView = ({
  groupType = 'organization',
  scopeParentId = null,
  teamGroupNameIncludes = null,
  restrictNestedGroupsToUserAccess = false,
  canCreateEntities = false,
  canAddMembers = false,
  platformOwnerViewer = false
}) => {
  const { user, token, fetchWithAuth, userGroupsPeriods, currentRole, availableUgp } = useAuth();
  const pageConfig = usePageConfig();
  const groupsPageConfiguration = pageConfig?.configuration || {};
  const {
    canCreateOrganizations,
    createEntitiesOrgWide,
    canViewDerivedGroups
  } = getGroupsPageCapabilities(groupsPageConfiguration);
  const entityLabel = groupType === 'organization' ? 'Organization' : 'School';
  const entityLabelPlural = groupType === 'organization' ? 'Organizations' : 'Schools';
  const [allSchools, setAllSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orgListSearch, setOrgListSearch] = useState('');
  const [relatedListSearch, setRelatedListSearch] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSchoolMostRecentGpId, setSelectedSchoolMostRecentGpId] = useState(null);
  const [relatedGroupsRaw, setRelatedGroupsRaw] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [selectedRelatedGroup, setSelectedRelatedGroup] = useState(null);
  // Map camera focus (continuous web tree). Not a folder remount root.
  const [groupsBrowseFocus, setGroupsBrowseFocus] = useState(null);
  const [mapCameraNonce, setMapCameraNonce] = useState(0);
  const [selectedRelatedPeriodId, setSelectedRelatedPeriodId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [derivedGuardiansData, setDerivedGuardiansData] = useState(null);
  const [derivedGuardiansError, setDerivedGuardiansError] = useState(null);
  const [loadingDerivedGuardians, setLoadingDerivedGuardians] = useState(false);
  const [hasDerivedGuardians, setHasDerivedGuardians] = useState(false);
  const [derivedParentGpIds, setDerivedParentGpIds] = useState([]);
  const hasRestoredState = useRef(false);
  const [showCreateSchoolModal, setShowCreateSchoolModal] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [createSchoolFormData, setCreateSchoolFormData] = useState(() =>
    getEmptyCreateEntityForm('organization', { periods: [] })
  );
  const [creatingSchool, setCreatingSchool] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [createGroupFormData, setCreateGroupFormData] = useState(() =>
    getEmptyCreateEntityForm('group', { periods: [] })
  );
  const [createGroupPeriodReady, setCreateGroupPeriodReady] = useState(false);
  const createGroupFormFieldsRef = useRef(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createGroupLoadingMessage, setCreateGroupLoadingMessage] = useState('Creating group…');
  const [createGroupLink, setCreateGroupLink] = useState({
    parentGpId: null,
    anchorGroup: null,
    parentGroupName: null,
    parentPeriodLabel: null,
    parentAttachGpId: null,
    addMode: 'team_period'
  });
  const [createGroupRoleTemplateId, setCreateGroupRoleTemplateId] = useState(
    GROUP_ROLE_TEMPLATE_NONE
  );
  // 'extending' | 'anchor' — extending is always the safe default; anchor is a
  // deliberate opt-in for primary identity/role groups (Students, Teachers).
  const [createGroupMembershipKind, setCreateGroupMembershipKind] = useState('extending');
  const [nestedTeamPeriodChoice, setNestedTeamPeriodChoice] = useState('parent');
  const [createGroupBatchMode, setCreateGroupBatchMode] = useState(false);
  const [createGroupBatchNamesText, setCreateGroupBatchNamesText] = useState('');
  const [cornerNotice, setCornerNotice] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [memberActionMessage, setMemberActionMessage] = useState('Working…');
  const [availableUsersList, setAvailableUsersList] = useState([]);
  const [existingUserEmails, setExistingUserEmails] = useState([]);
  const [loadingUsersForAdd, setLoadingUsersForAdd] = useState(false);
  const [showAllOrganizations, setShowAllOrganizations] = useState(false);
  const [orgContextReady, setOrgContextReady] = useState(false);
  const hasAutoEnteredOrg = useRef(false);

  const showDerivedGuardiansNode =
    canViewDerivedGroups && hasDerivedGuardians && derivedParentGpIds.length > 0;

  const checkDerivedGuardiansAvailability = useCallback(async (orgGpId) => {
    if (!orgGpId) {
      setHasDerivedGuardians(false);
      setDerivedParentGpIds([]);
      return;
    }
    try {
      const response = await fetch(
        `/api/relationships/derived-guardians?org_groups_periods_id=${encodeURIComponent(orgGpId)}&count_only=true`
      );
      const data = await response.json();
      if (data.success && data.data) {
        const placement = (data.data.placement_parent_gp_ids || []).map(String);
        const count = data.data.distinct_guardian_count || 0;
        setHasDerivedGuardians(count > 0 && placement.length > 0);
        setDerivedParentGpIds(placement);
      } else {
        setHasDerivedGuardians(false);
        setDerivedParentGpIds([]);
      }
    } catch (error) {
      console.error('Error checking derived guardians availability:', error);
      setHasDerivedGuardians(false);
      setDerivedParentGpIds([]);
    }
  }, []);

  useEffect(() => {
    if (!canViewDerivedGroups || !selectedSchoolMostRecentGpId) {
      setHasDerivedGuardians(false);
      setDerivedParentGpIds([]);
      return;
    }
    checkDerivedGuardiansAvailability(selectedSchoolMostRecentGpId);
  }, [canViewDerivedGroups, selectedSchoolMostRecentGpId, checkDerivedGuardiansAvailability]);

  const canBrowseAll = useMemo(
    () => canBrowseAllOrganizations(availableUgp, userGroupsPeriods, currentRole),
    [availableUgp, userGroupsPeriods, currentRole]
  );

  const systemOrgContext = useMemo(
    () => isSystemOrganizationContext(currentRole),
    [currentRole]
  );

  const showOrgPicker =
    !scopeParentId &&
    !selectedSchool &&
    orgContextReady &&
    (showAllOrganizations || (canBrowseAll && systemOrgContext));

  const relatedGroups = useMemo(() => {
    let list = relatedGroupsRaw;
    const applyMembershipFilter =
      restrictNestedGroupsToUserAccess ||
      userHasSameLevelBrowseGrant(
        userGroupsPeriods,
        relatedGroupsRaw,
        selectedSchoolMostRecentGpId,
        groupsPageConfiguration
      );
    if (applyMembershipFilter) {
      list = filterNestedGroupsToUserAccess(
        list,
        userGroupsPeriods,
        selectedSchool?.id,
        selectedSchoolMostRecentGpId,
        { availableUgp, userGroupsPeriods, currentRole, pageConfiguration: groupsPageConfiguration }
      );
    }
    return filterGroupsByNamePolicy(
      list,
      {
        includes: teamGroupNameIncludes,
        excludes: groupsPageConfiguration?.role_group_name_excludes,
        excludeGroupIds: groupsPageConfiguration?.role_group_id_excludes
      },
      selectedSchool?.id,
      selectedSchoolMostRecentGpId
    );
  }, [
    relatedGroupsRaw,
    restrictNestedGroupsToUserAccess,
    groupsPageConfiguration,
    userGroupsPeriods,
    selectedSchool?.id,
    selectedSchoolMostRecentGpId,
    availableUgp,
    currentRole,
    teamGroupNameIncludes
  ]);

  const canCreateOrganizationsHere = useMemo(
    () => canCreateEntities && canCreateOrganizations,
    [canCreateEntities, canCreateOrganizations]
  );

  const handleOrgLogoUploaded = useCallback((groupId, url) => {
    setAllSchools((prev) =>
      prev.map((s) => (String(s.id) === String(groupId) ? { ...s, logo_url: url } : s))
    );
    setSelectedSchool((prev) =>
      prev && String(prev.id) === String(groupId) ? { ...prev, logo_url: url } : prev
    );
  }, []);

  const handleOrgLogoUploadError = useCallback((message) => {
    setCornerNotice({
      isOpen: true,
      title: 'Logo upload failed',
      text: message,
      position: 'bottom-left',
      containerColor: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
      textColor: '#991b1b',
      showOverlay: false,
      dismissOnClick: true,
      autoClose: true,
      viewTime: 6000
    });
  }, []);

  const showAddOrganizationEmpty =
    canCreateOrganizationsHere &&
    showOrgPicker &&
    !isLoading &&
    orgContextReady &&
    allSchools.length === 0;

  const canCreateAtOrgLevel = useMemo(() => {
    if (!selectedSchool?.id) return false;
    return canCreateInGroupContext({
      configuration: groupsPageConfiguration,
      parentGroupId: selectedSchool.id,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool.id,
      orgGpId: selectedSchoolMostRecentGpId,
      availableUgp,
      currentRole,
      platformOwnerViewer
    });
  }, [
    selectedSchool?.id,
    groupsPageConfiguration,
    userGroupsPeriods,
    relatedGroupsRaw,
    selectedSchoolMostRecentGpId,
    availableUgp,
    currentRole,
    platformOwnerViewer
  ]);

  const canCreateForCurrentBrowseScope = useMemo(() => {
    if (!selectedSchool?.id) return false;
    if (createEntitiesOrgWide && canBrowseAll) {
      return !nonPlatformOwnerBlockedOnOwnerTeam({
        platformOwnerViewer,
        parentGroupId: selectedSchool.id,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool.id,
        orgGpId: selectedSchoolMostRecentGpId
      });
    }
    if (canCreateAtOrgLevel) return true;
    // Continuous map: allow add ghosts if the user can create under any team.
    return (relatedGroupsRaw || []).some((g) =>
      canCreateInGroupContext({
        configuration: groupsPageConfiguration,
        parentGroupId: g.id,
        targetGroupId: g.id,
        userGroupsPeriods,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool.id,
        orgGpId: selectedSchoolMostRecentGpId,
        availableUgp,
        currentRole,
        platformOwnerViewer
      })
    );
  }, [
    groupsPageConfiguration,
    userGroupsPeriods,
    relatedGroupsRaw,
    selectedSchool?.id,
    selectedSchoolMostRecentGpId,
    availableUgp,
    currentRole,
    createEntitiesOrgWide,
    canBrowseAll,
    canCreateAtOrgLevel,
    platformOwnerViewer
  ]);

  const canAddMembersForSelectedGroup = useMemo(() => {
    if (!selectedRelatedGroup?.id || !selectedSchool) return false;
    if (isDerivedGuardiansGroup(selectedRelatedGroup)) return false;
    return canAddMembersToGroup({
      configuration: groupsPageConfiguration,
      groupId: selectedRelatedGroup.id,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool.id,
      orgGpId: selectedSchoolMostRecentGpId,
      platformOwnerViewer
    });
  }, [
    selectedRelatedGroup?.id,
    selectedSchool,
    groupsPageConfiguration,
    userGroupsPeriods,
    relatedGroupsRaw,
    selectedSchoolMostRecentGpId,
    platformOwnerViewer
  ]);

  const derivedPlacementGroupId = useMemo(() => {
    if (!isDerivedGuardiansGroup(selectedRelatedGroup) || !selectedSchool) return null;
    const parentGpId = getDerivedGuardiansParentGpId(selectedRelatedGroup);
    return (
      resolveGroupIdForGpId(
        relatedGroupsRaw,
        selectedSchool.id,
        selectedSchoolMostRecentGpId,
        parentGpId
      ) || selectedSchool.id
    );
  }, [
    selectedRelatedGroup,
    selectedSchool,
    relatedGroupsRaw,
    selectedSchoolMostRecentGpId
  ]);

  const canOpenMemberProfilesForSelectedGroup = useMemo(() => {
    if (!selectedRelatedGroup?.id || !selectedSchool) return false;
    if (isDerivedGuardiansGroup(selectedRelatedGroup)) {
      return canOpenMemberProfilesInGroup({
        configuration: groupsPageConfiguration,
        groupId: derivedPlacementGroupId || selectedSchool.id,
        userGroupsPeriods,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool.id,
        orgGpId: selectedSchoolMostRecentGpId,
        platformOwnerViewer
      });
    }
    return canOpenMemberProfilesInGroup({
      configuration: groupsPageConfiguration,
      groupId: selectedRelatedGroup.id,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool.id,
      orgGpId: selectedSchoolMostRecentGpId,
      platformOwnerViewer
    });
  }, [
    selectedRelatedGroup?.id,
    selectedSchool,
    derivedPlacementGroupId,
    groupsPageConfiguration,
    userGroupsPeriods,
    relatedGroupsRaw,
    selectedSchoolMostRecentGpId,
    platformOwnerViewer
  ]);

  const memberVisibilityParams = useMemo(() => {
    if (!selectedRelatedGroup?.id || !selectedSchool) return null;
    const groupId = isDerivedGuardiansGroup(selectedRelatedGroup)
      ? derivedPlacementGroupId || selectedSchool.id
      : selectedRelatedGroup.id;
    return {
      configuration: groupsPageConfiguration,
      groupId,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool.id,
      orgGpId: selectedSchoolMostRecentGpId,
      platformOwnerViewer
    };
  }, [
    selectedRelatedGroup?.id,
    selectedSchool,
    derivedPlacementGroupId,
    groupsPageConfiguration,
    userGroupsPeriods,
    relatedGroupsRaw,
    selectedSchoolMostRecentGpId,
    platformOwnerViewer
  ]);

  const canViewGroupMembersForSelectedGroup = useMemo(() => {
    if (!memberVisibilityParams) return false;
    return canViewGroupMembersInGroup(memberVisibilityParams);
  }, [memberVisibilityParams]);

  const canViewMemberDetailsForSelectedGroup = useMemo(() => {
    if (!memberVisibilityParams) return false;
    return canViewMemberDetailsInGroup(memberVisibilityParams);
  }, [memberVisibilityParams]);

  const canViewMemberAccessForSelectedGroup = useMemo(() => {
    if (!memberVisibilityParams) return false;
    return canViewMemberAccessInGroup(memberVisibilityParams);
  }, [memberVisibilityParams]);

  useEffect(() => {
    loadSchools();
  }, [
    user,
    token,
    userGroupsPeriods,
    groupType,
    currentRole?.parent_group_type,
    currentRole?.parent_group_name,
    currentRole?.parent_groups_periods_id
  ]);

  const loadOrgSchoolYearPeriods = async (organizationGroupId) => {
    if (!organizationGroupId) {
      setPeriods([]);
      return;
    }
    try {
      // Include Permanent — teams like Sports / Basketball may enroll on it.
      const params = new URLSearchParams({
        organization_group_id: String(organizationGroupId)
      });
      const response = await fetch(`/api/periods?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setPeriods(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load organization periods:', error);
      setPeriods([]);
    }
  };

  useEffect(() => {
    if (selectedSchool?.id) {
      loadOrgSchoolYearPeriods(selectedSchool.id);
    } else {
      setPeriods([]);
    }
  }, [selectedSchool?.id]);

  const openCreateOrganizationModal = () => {
    if (!canCreateOrganizationsHere) return;
    setCreateSchoolFormData(getEmptyCreateEntityForm('organization', { periods }));
    setShowCreateSchoolModal(true);
  };

  const buildCreateGroupPeriodDescriptionContext = useCallback(
    (link, periodDef = null) => {
      const anchor = link?.anchorGroup;
      if (!anchor) return null;
      const parentCtx = resolveParentEnrollmentContext(anchor, relatedGroups);
      if (link.addMode === 'team_period') {
        return {
          mode: 'team_period',
          teamName: anchor.name,
          parentTeamName: parentCtx.parentTeamName,
          parentPeriodLabel: parentCtx.parentPeriodLabel
        };
      }
      const attachGp = (anchor.periods || []).find(
        (gp) => String(gp.id) === String(link.parentAttachGpId)
      );
      const attachLabel = attachGp
        ? formatTeamGroupsPeriodOption(attachGp)
        : link.parentPeriodLabel || '';
      return {
        mode: 'nested_team',
        parentTeamName: anchor.name,
        parentPeriodLabel: attachLabel
      };
    },
    [relatedGroups]
  );

  const createGroupPeriodOptions = useMemo(
    () =>
      resolveCreateGroupPeriodOptions({
        periods,
        anchorGroup: createGroupLink.anchorGroup,
        addMode: createGroupLink.addMode,
        parentAttachGpId: createGroupLink.parentAttachGpId
      }),
    [
      periods,
      createGroupLink.anchorGroup,
      createGroupLink.addMode,
      createGroupLink.parentAttachGpId
    ]
  );

  // Permanent may only be a group's sole period — hide create option when attaching to a team that already has periods.
  const allowPermanentPeriodCreate = useMemo(() => {
    if (createGroupLink.addMode === 'team_period' && createGroupLink.anchorGroup) {
      return !(createGroupLink.anchorGroup.periods?.length > 0);
    }
    return true;
  }, [createGroupLink.addMode, createGroupLink.anchorGroup]);

  // If the team is already on Permanent, no further periods can be attached.
  const allowNewPeriodCreate = useMemo(() => {
    if (createGroupLink.addMode !== 'team_period' || !createGroupLink.anchorGroup) return true;
    return !(createGroupLink.anchorGroup.periods || []).some((gp) => {
      const def = periodDefinitionFromTeamGp(gp);
      return def ? isPermanentPeriod(def) : isPermanentPeriod(gp);
    });
  }, [createGroupLink.addMode, createGroupLink.anchorGroup]);

  const nestedTeamSuggestedPeriodId = useMemo(() => {
    if (
      !createGroupLink.anchorGroup ||
      createGroupLink.addMode !== 'nested_team' ||
      !createGroupLink.parentAttachGpId
    ) {
      return '';
    }
    return (
      resolveNestedTeamPeriodFromAttach(
        createGroupLink.anchorGroup,
        createGroupLink.parentAttachGpId,
        periods
      )?.periodDefId || ''
    );
  }, [
    createGroupLink.anchorGroup,
    createGroupLink.addMode,
    createGroupLink.parentAttachGpId,
    periods
  ]);

  const nestedParentPeriod = useMemo(() => {
    if (!createGroupLink.anchorGroup || !createGroupLink.parentAttachGpId) return null;
    return (
      resolveNestedTeamPeriodFromAttach(
        createGroupLink.anchorGroup,
        createGroupLink.parentAttachGpId,
        periods
      )?.periodDef || null
    );
  }, [createGroupLink.anchorGroup, createGroupLink.parentAttachGpId, periods]);

  const applyNestedTeamPeriodToForm = useCallback(
    (link) => {
      if (!link?.anchorGroup || link.addMode !== 'nested_team' || !link.parentAttachGpId) {
        return;
      }
      const resolved = resolveNestedTeamPeriodFromAttach(
        link.anchorGroup,
        link.parentAttachGpId,
        periods
      );
      if (!resolved) return;
      const descContext = buildCreateGroupPeriodDescriptionContext(link, resolved.periodDef);
      setCreateGroupFormData((formPrev) => ({
        ...formPrev,
        periodId: resolved.periodDefId,
        ...buildGroupPeriodComboSuggests(
          formPrev.name,
          resolved.periodDef,
          selectedSchool?.name,
          descContext
        )
      }));
    },
    [buildCreateGroupPeriodDescriptionContext, periods, selectedSchool?.name]
  );

  const openCreateGroupModal = (context = {}) => {
    const targetId = context.anchorGroup?.id || selectedSchool?.id || null;
    const parentTargetId = context.anchorGroup?.id || selectedSchool?.id || null;
    const canCreateHere = canCreateInGroupContext({
      configuration: groupsPageConfiguration,
      targetGroupId: targetId,
      parentGroupId: parentTargetId,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool?.id,
      orgGpId: selectedSchoolMostRecentGpId,
      availableUgp,
      currentRole,
      platformOwnerViewer
    });
    if (!canCreateHere) return;
    const anchor = context.anchorGroup ?? null;
    const teamPeriods = anchor?.periods || [];
    const selectedOnTeam = teamPeriods.some(
      (gp) => String(gp.id) === String(selectedRelatedPeriodId)
    )
      ? String(selectedRelatedPeriodId)
      : null;
    const defaultAttach =
      selectedOnTeam ||
      (teamPeriods.length === 1 ? String(teamPeriods[0].id) : null) ||
      (teamPeriods[0] ? String(teamPeriods[0].id) : null);
    // Ghost cards pass addMode (and parentAttachGpId for nested-team ghosts on
    // a specific period card) so the sheet opens with that choice preselected.
    const initialAddMode =
      context.addMode ?? (anchor ? 'team_period' : 'nested_team');
    const contextAttach =
      context.parentAttachGpId != null ? String(context.parentAttachGpId) : null;
    const initialLink = {
      parentGpId: context.parentGpId ?? null,
      anchorGroup: anchor,
      parentGroupName: context.parentGroupName ?? null,
      parentPeriodLabel: context.parentPeriodLabel ?? null,
      parentAttachGpId: contextAttach ?? context.parentGpId ?? defaultAttach,
      addMode: initialAddMode
    };
    setCreateGroupLink(initialLink);
    const attachGp = initialLink.parentAttachGpId
      ? (anchor?.periods || []).find(
          (gp) => String(gp.id) === String(initialLink.parentAttachGpId)
        )
      : null;
    const suggestedNewTeamPeriodId =
      initialAddMode === 'nested_team' && initialLink.parentAttachGpId && anchor
        ? resolveNestedTeamPeriodFromAttach(
            anchor,
            initialLink.parentAttachGpId,
            periods
          )?.periodDefId || ''
        : '';
    const emptyForm = getEmptyCreateEntityForm('group', {
      periods,
      school: selectedSchool,
      schoolGpId: selectedSchoolMostRecentGpId,
      requireExplicitPeriod: Boolean(anchor)
    });
    const descContext = buildCreateGroupPeriodDescriptionContext(initialLink);
    const initialPeriodDef = suggestedNewTeamPeriodId
      ? periods.find((p) => String(p.id) === String(suggestedNewTeamPeriodId)) ||
        periodDefinitionFromTeamGp(attachGp)
      : null;
    const subjectName =
      initialAddMode === 'team_period' ? anchor?.name : emptyForm.name;
    const initialComboSuggests =
      initialPeriodDef && descContext
        ? buildGroupPeriodComboSuggests(
            subjectName,
            initialPeriodDef,
            selectedSchool?.name,
            descContext
          )
        : null;
    setCreateGroupFormData({
      ...emptyForm,
      periodId: suggestedNewTeamPeriodId || emptyForm.periodId,
      ...(initialComboSuggests || {})
    });
    setCreateGroupPeriodReady(false);
    setCreateGroupMembershipKind('extending');
    setNestedTeamPeriodChoice('parent');
    setCreateGroupBatchMode(false);
    setCreateGroupBatchNamesText('');
    setShowCreateGroupModal(true);
    if (selectedSchool?.id) {
      loadOrgSchoolYearPeriods(selectedSchool.id);
    }
  };

  const resolveCreateGroupParentGpId = useCallback(() => {
    if (createGroupLink.anchorGroup && createGroupLink.addMode === 'nested_team') {
      return createGroupLink.parentAttachGpId || null;
    }
    if (createGroupLink.parentGpId) return createGroupLink.parentGpId;
    return selectedSchoolMostRecentGpId;
  }, [createGroupLink, selectedSchoolMostRecentGpId]);

  const createGroupBatchNames = useMemo(
    () => parseBatchGroupNames(createGroupBatchNamesText),
    [createGroupBatchNamesText]
  );

  const createGroupAllowsBatch =
    !createGroupLink.anchorGroup || createGroupLink.addMode === 'nested_team';

  const createGroupModalSubtext = useMemo(() => {
    if (createGroupLink.anchorGroup) {
      const name =
        createGroupLink.parentGroupName || createGroupLink.anchorGroup?.name || 'this team';
      return `Add another period under ${name}, or nest a group under a ${name}–period combination.`;
    }
    const orgName = selectedSchool?.name || 'this organization';
    return `Use this popup to create one or multiple groups under ${orgName}.`;
  }, [createGroupLink.anchorGroup, createGroupLink.parentGroupName, selectedSchool?.name]);

  const createGroupSubmitReady = useMemo(() => {
    if (!createGroupPeriodReady) return false;
    if (createGroupLink.anchorGroup && createGroupLink.addMode === 'team_period') {
      return true;
    }
    if (
      createGroupLink.anchorGroup &&
      createGroupLink.addMode === 'nested_team' &&
      !createGroupLink.parentAttachGpId
    ) {
      return false;
    }
    if (createGroupBatchMode && createGroupAllowsBatch) {
      return createGroupBatchNames.length > 0;
    }
    if (!createGroupFormData.name?.trim()) return false;
    return true;
  }, [
    createGroupLink,
    createGroupFormData.name,
    createGroupPeriodReady,
    createGroupBatchMode,
    createGroupAllowsBatch,
    createGroupBatchNames.length
  ]);

  const createGroupPrimaryLabel = useMemo(() => {
    if (creatingGroup) return 'Saving…';
    if (createGroupLink.anchorGroup && createGroupLink.addMode === 'team_period') {
      return 'Add period';
    }
    if (createGroupBatchMode && createGroupAllowsBatch) {
      const n = createGroupBatchNames.length;
      return n > 0 ? `Create ${n} groups` : 'Create groups';
    }
    return 'Create';
  }, [
    createGroupLink,
    creatingGroup,
    createGroupBatchMode,
    createGroupAllowsBatch,
    createGroupBatchNames.length
  ]);

  const createGroupBatchPreview = useMemo(() => {
    if (!createGroupBatchMode || !createGroupAllowsBatch || createGroupBatchNames.length === 0) {
      return null;
    }
    const sampleName = createGroupBatchNames[0];
    const periodDef =
      periods.find((p) => String(p.id) === String(createGroupFormData.periodId)) ||
      nestedParentPeriod;
    const descContext = buildCreateGroupPeriodDescriptionContext(createGroupLink);
    const about = suggestGroupDescription(sampleName, selectedSchool?.name, descContext);
    const combo = periodDef
      ? buildGroupPeriodComboSuggests(
          sampleName,
          periodDef,
          selectedSchool?.name,
          descContext
        )
      : { groupPeriodTitle: '', groupPeriodDescription: '' };
    return {
      sampleName,
      about,
      groupPeriodTitle: combo.groupPeriodTitle,
      groupPeriodDescription: combo.groupPeriodDescription,
      total: createGroupBatchNames.length
    };
  }, [
    createGroupBatchMode,
    createGroupAllowsBatch,
    createGroupBatchNames,
    createGroupFormData.periodId,
    nestedParentPeriod,
    periods,
    createGroupLink,
    selectedSchool?.name,
    buildCreateGroupPeriodDescriptionContext
  ]);

  const handlePeriodCreated = (createdPeriod) => {
    setPeriods((prev) => {
      const exists = prev.some((p) => String(p.id) === String(createdPeriod.id));
      if (exists) return prev;
      return [createdPeriod, ...prev];
    });
  };

  const resetCreateOrganizationForm = () => {
    setCreateSchoolFormData(getEmptyCreateEntityForm('organization', { periods }));
  };

  const resetCreateGroupForm = () => {
    setCreateGroupLink({
      parentGpId: null,
      anchorGroup: null,
      parentGroupName: null,
      parentPeriodLabel: null,
      parentAttachGpId: null,
      addMode: 'team_period'
    });
    setCreateGroupFormData(
      getEmptyCreateEntityForm('group', {
        periods,
        school: selectedSchool,
        schoolGpId: selectedSchoolMostRecentGpId
      })
    );
    setCreateGroupPeriodReady(false);
    setCreateGroupRoleTemplateId(GROUP_ROLE_TEMPLATE_NONE);
    setCreateGroupMembershipKind('extending');
    setNestedTeamPeriodChoice('parent');
    setCreateGroupBatchMode(false);
    setCreateGroupBatchNamesText('');
  };

  const applyCreateGroupRoleTemplate = async (groupsPeriodsId) => {
    if (!createGroupRoleTemplateId || !groupsPeriodsId || !fetchWithAuth) {
      return { ok: true, skipped: true };
    }
    setCreateGroupLoadingMessage('Applying role template…');
    return applyGroupRoleTemplatePermissions({
      templateId: createGroupRoleTemplateId,
      groupsPeriodsId,
      fetchWithAuth,
      orgContext: {
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool?.id,
        orgGpId: selectedSchoolMostRecentGpId
      }
    });
  };

  const warnRoleTemplateApplyFailure = (applyResult) => {
    if (!applyResult || applyResult.ok || applyResult.skipped) return;
    const pages = (applyResult.failures || []).map((f) => f.page).join(', ');
    alert(
      `Group-period was created, but the ${applyResult.templateLabel || 'role'} template could not be fully applied` +
        (pages ? ` (${pages})` : '') +
        '. Add permissions manually in Page Permissions Manager.'
    );
  };

  // Restore state from URL parameters after schools are loaded
  useEffect(() => {
    if (allSchools.length > 0 && !hasRestoredState.current) {
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get('returnTo');
      if (returnTo === 'members') {
        const schoolId = params.get('schoolId');
        const groupId = params.get('groupId');
        const periodId = params.get('periodId');
        if (schoolId && groupId && periodId) {
          hasRestoredState.current = true;
          restoreStateFromParams(schoolId, groupId, periodId);
        }
      }
    }
  }, [allSchools]);

  const restoreStateFromParams = async (schoolId, groupId, periodId) => {
    try {
      // Find the school
      const school = allSchools.find(s => String(s.id) === String(schoolId));
      if (!school) {
        console.error('School not found:', schoolId);
        return;
      }

      // Set the school first
      setSelectedSchool(school);
      const mostRecent = school?.periods?.[0] || null;
      setSelectedSchoolMostRecentGpId(mostRecent ? mostRecent.id : null);
      
      // Load related groups
      if (mostRecent?.id) {
        const response = await fetch(
          `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(mostRecent.id)}`
        );
        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          const groups = data.data;
          setRelatedGroupsRaw(groups);
          
          // Find and select the group
          const relatedGroup = groups.find(g => String(g.id) === String(groupId));
          if (relatedGroup) {
            setSelectedRelatedGroup(relatedGroup);
            setSelectedRelatedPeriodId(periodId);
            // Load members for the selected period
            await loadMembersForPeriod(relatedGroup.id, periodId);
          } else {
            console.error('Related group not found:', groupId);
          }
        }
      } else {
        console.error('No most recent period found for school');
      }
    } catch (error) {
      console.error('Error restoring state from params:', error);
    } finally {
      setOrgContextReady(true);
    }
  };

  const loadSchools = async () => {
    setIsLoading(true);
    try {
      const url = `/api/groups-periods${groupType ? `?group_type=${encodeURIComponent(groupType)}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        let schools = mapGroupsPeriodsRowsToGroups(data.data, groupType);

        if (isSystemOrganizationContext(currentRole)) {
          const systemRes = await fetch('/api/groups-periods?group_type=system');
          const systemData = await systemRes.json();
          if (systemData.success) {
            const systemGroups = mapGroupsPeriodsRowsToGroups(systemData.data, 'system');
            schools = prependSystemGroupsToOrganizationList(schools, systemGroups);
          }
        }

        setAllSchools(schools);
      }
    } catch (error) {
      console.error('Failed to load schools:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchoolClick = async (school) => {
    const mostRecent = school?.periods?.[0] || null;
    setSelectedSchool(school);
    setSelectedSchoolMostRecentGpId(mostRecent ? mostRecent.id : null);
    setGroupsBrowseFocus(null);
    setSelectedRelatedGroup(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
    setShowAllOrganizations(false);
    if (mostRecent?.id) {
      await loadRelatedGroupsForParentGp(mostRecent.id);
    } else {
      setRelatedGroupsRaw([]);
    }
  };

  const loadRelatedGroupsForParentGp = async (parentGroupsPeriodsId) => {
    setLoadingRelated(true);
    try {
      const response = await fetch(
        `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(parentGroupsPeriodsId)}`
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const groups = data.data;
        setRelatedGroupsRaw(groups);
        setGroupsBrowseFocus((prev) => {
          if (!prev?.group?.id) return prev;
          const fresh = groups.find((g) => String(g.id) === String(prev.group.id));
          return fresh ? { group: fresh } : prev;
        });
        setSelectedRelatedGroup((prev) => {
          if (!prev?.id) return prev;
          const fresh = groups.find((g) => String(g.id) === String(prev.id));
          return fresh || prev;
        });
        return groups;
      }
      setRelatedGroupsRaw([]);
      return [];
    } catch (error) {
      console.error('Failed to load related groups:', error);
      setRelatedGroupsRaw([]);
      return [];
    } finally {
      setLoadingRelated(false);
    }
  };

  useEffect(() => {
    if (isLoading || hasRestoredState.current || hasAutoEnteredOrg.current) return;
    if (!allSchools.length) return;

    const finish = () => {
      hasAutoEnteredOrg.current = true;
      setOrgContextReady(true);
    };

    if (scopeParentId) {
      const school = findOrganizationByOrgGpId(allSchools, scopeParentId);
      if (school) {
        const enterScoped = async () => {
          const mostRecent = school?.periods?.[0] || null;
          const gpId = scopeParentId || mostRecent?.id || null;
          setSelectedSchool(school);
          setSelectedSchoolMostRecentGpId(gpId);
          setGroupsBrowseFocus(null);
          setSelectedRelatedGroup(null);
          setSelectedRelatedPeriodId(null);
          setGroupMembers([]);
          setShowAllOrganizations(false);
          if (gpId) await loadRelatedGroupsForParentGp(gpId);
          else setRelatedGroupsRaw([]);
        };
        enterScoped().finally(finish);
        return;
      }
    }

    if (canBrowseAll && systemOrgContext) {
      finish();
      return;
    }

    let school = findOrganizationInList(allSchools, currentRole);
    if (!school && allSchools.length === 1) {
      school = allSchools[0];
    }
    if (school) {
      handleSchoolClick(school).finally(finish);
      return;
    }

    finish();
  }, [isLoading, allSchools, scopeParentId, currentRole, canBrowseAll, systemOrgContext]);

  useEffect(() => {
    if (!isLoading && allSchools.length === 0) {
      setOrgContextReady(true);
    }
  }, [isLoading, allSchools.length]);

  const goToOrganizations = () => {
    setSelectedSchool(null);
    setSelectedSchoolMostRecentGpId(null);
    setSelectedRelatedGroup(null);
    setGroupsBrowseFocus(null);
    setSelectedRelatedPeriodId(null);
    setRelatedGroupsRaw([]);
    setGroupMembers([]);
    setOrgListSearch('');
    setRelatedListSearch('');
    setShowAllOrganizations(true);
  };

  const goToMyGroups = () => {
    setShowAllOrganizations(false);
    setSelectedRelatedGroup(null);
    setGroupsBrowseFocus(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
    setRelatedListSearch('');
    const school = findOrganizationInList(allSchools, currentRole);
    if (school) {
      handleSchoolClick(school);
      return;
    }
    goToOrganizations();
  };

  const goToOrgGroupsTop = () => {
    setGroupsBrowseFocus(null);
    setMapCameraNonce((n) => n + 1);
    setSelectedRelatedGroup(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
    setDerivedGuardiansData(null);
    setDerivedGuardiansError(null);
    setRelatedListSearch('');
  };

  const goToRelatedGroups = () => {
    setSelectedRelatedGroup(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
    setDerivedGuardiansData(null);
    setDerivedGuardiansError(null);
    setRelatedListSearch('');
  };

  const filteredSchools = useMemo(() => {
    const q = orgListSearch.trim().toLowerCase();
    if (!q) return allSchools;
    return allSchools.filter((g) => {
      const name = (g.name || '').toLowerCase();
      const desc = (g.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [allSchools, orgListSearch]);

  const loadDerivedGuardians = useCallback(async (orgGpId, parentGpId = null) => {
    if (!orgGpId) {
      setDerivedGuardiansData(null);
      setDerivedGuardiansError('Organization period is required.');
      return;
    }
    setLoadingDerivedGuardians(true);
    setDerivedGuardiansError(null);
    try {
      const params = new URLSearchParams({
        org_groups_periods_id: orgGpId
      });
      if (parentGpId) {
        params.set('parent_groups_periods_id', parentGpId);
      }
      const response = await fetch(`/api/relationships/derived-guardians?${params}`);
      const data = await response.json();
      if (data.success && data.data) {
        setDerivedGuardiansData(data.data);
      } else {
        setDerivedGuardiansData(null);
        setDerivedGuardiansError(data.message || 'Failed to load guardians.');
      }
    } catch (error) {
      console.error('Error loading derived guardians:', error);
      setDerivedGuardiansData(null);
      setDerivedGuardiansError('Failed to load guardians.');
    } finally {
      setLoadingDerivedGuardians(false);
    }
  }, []);

  const handleSelectGroupNode = async (node) => {
    // Org root card — zoom out to fit the whole map.
    if (node?.nodeType === 'organization') {
      setGroupsBrowseFocus(null);
      setMapCameraNonce((n) => n + 1);
      setSelectedRelatedGroup(null);
      setSelectedRelatedPeriodId(null);
      setGroupMembers([]);
      setDerivedGuardiansData(null);
      setDerivedGuardiansError(null);
      return;
    }
    const group = node?.group;
    if (!group) return;
    if (isDerivedGuardiansGroup(group)) {
      if (!canViewDerivedGroups || !hasDerivedGuardians) return;
      const parentGpId =
        node.derivedParentGpId ||
        getDerivedGuardiansParentGpId(group) ||
        null;
      setGroupsBrowseFocus(null);
      setSelectedRelatedGroup(group);
      setSelectedRelatedPeriodId(null);
      setGroupMembers([]);
      if (selectedSchoolMostRecentGpId) {
        await loadDerivedGuardians(selectedSchoolMostRecentGpId, parentGpId);
      }
      return;
    }
    setDerivedGuardiansData(null);
    setDerivedGuardiansError(null);
    // Camera focus only — keep the continuous tree mounted.
    setGroupsBrowseFocus({ group });
    setMapCameraNonce((n) => n + 1);
    setSelectedRelatedGroup(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
  };

  const handleSelectPeriodNode = async (node) => {
    const group = node?.group;
    const periodGpId = node?.periodGpId || node?.period?.id;
    if (!group || !periodGpId) return;
    setDerivedGuardiansData(null);
    setDerivedGuardiansError(null);
    setSelectedRelatedGroup(group);
    setSelectedRelatedPeriodId(periodGpId);
    await loadMembersForPeriod(group.id, periodGpId);
  };

  const handleMemberPeriodChange = async (periodOrId) => {
    const periodId = periodOrId?.id ?? periodOrId;
    setSelectedRelatedPeriodId(periodId);
    if (selectedRelatedGroup?.id && periodId) {
      await loadMembersForPeriod(selectedRelatedGroup.id, periodId);
    }
  };

  const loadMembersForPeriod = async (groupId, periodId) => {
    setLoadingMembers(true);
    try {
      const response = await fetch(`/api/groups/${groupId}`);
      const data = await response.json();
      if (data.success && data.data && Array.isArray(data.data.users)) {
        const members = data.data.users.filter(
          (user) => String(user.groups_periods_id) === String(periodId)
        );
        setGroupMembers(members);
      } else {
        setGroupMembers([]);
      }
    } catch (error) {
      console.error('Failed to load group members:', error);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const enrollUserInPeriod = async (userId) => {
    if (
      !canAddMembersToGroup({
        configuration: groupsPageConfiguration,
        groupId: selectedRelatedGroup?.id,
        userGroupsPeriods,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool?.id,
        orgGpId: selectedSchoolMostRecentGpId,
        platformOwnerViewer
      })
    ) {
      const reason = ownerTeamActionBlockedReason({
        platformOwnerViewer,
        group: selectedRelatedGroup,
        groupId: selectedRelatedGroup?.id,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool?.id,
        orgGpId: selectedSchoolMostRecentGpId
      });
      throw new Error(ownerTeamActionBlockedMessage(reason || 'org'));
    }
    const period = selectedRelatedGroup.periods?.find(
      (p) => String(p.id) === String(selectedRelatedPeriodId)
    );
    const startDate = period?.start_date
      ? new Date(period.start_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    const endDate = period?.end_date
      ? new Date(period.end_date).toISOString().split('T')[0]
      : new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0];
    const response = await fetch('/api/user-groups-periods', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        user_id: userId,
        groups_periods_id: selectedRelatedPeriodId,
        start_date: startDate,
        end_date: endDate,
        is_active: true,
        override_period: false
      })
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to add user to group.');
    }
    return data.data?.id || null;
  };

  const handleAddExistingMember = async (userId) => {
    setMemberActionMessage('Adding member…');
    setAddingUser(true);
    try {
      await enrollUserInPeriod(userId);
      setShowAddUserModal(false);
      await loadMembersForPeriod(selectedRelatedGroup.id, selectedRelatedPeriodId);
    } catch (err) {
      throw err;
    } finally {
      setAddingUser(false);
    }
  };

  const handleCreateAndAddMember = async (userData) => {
    setMemberActionMessage('Creating user…');
    setAddingUser(true);
    try {
      const createRes = await fetch('/api/users/create-without-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(userData)
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData.success) {
        throw new Error(createData.message || 'Failed to create user.');
      }
      const userId = createData.user?.id;
      if (!userId) throw new Error('Created user has no id.');
      await enrollUserInPeriod(userId);
      setShowAddUserModal(false);
      await loadMembersForPeriod(selectedRelatedGroup.id, selectedRelatedPeriodId);
    } finally {
      setAddingUser(false);
    }
  };

  const handleBulkCreateAndAddMembers = async ({
    rows: usersToCreate = [],
    overwriteRows = [],
    ignoreDuplicates = false,
    overwriteExisting = false,
    overwriteColumns = [],
    skippedDuplicates = []
  }) => {
    if (!selectedRelatedPeriodId || !selectedRelatedGroup?.id) {
      throw new Error('Select a group and period before importing.');
    }
    if (!canAddMembersToGroup({
      configuration: groupsPageConfiguration,
      groupId: selectedRelatedGroup?.id,
      userGroupsPeriods,
      allGroups: relatedGroupsRaw,
      orgGroupId: selectedSchool?.id,
      orgGpId: selectedSchoolMostRecentGpId,
      platformOwnerViewer
    })) {
      const reason = ownerTeamActionBlockedReason({
        platformOwnerViewer,
        group: selectedRelatedGroup,
        groupId: selectedRelatedGroup?.id,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool?.id,
        orgGpId: selectedSchoolMostRecentGpId
      });
      throw new Error(ownerTeamActionBlockedMessage(reason || 'org'));
    }

    const period = selectedRelatedGroup.periods?.find(
      (p) => String(p.id) === String(selectedRelatedPeriodId)
    );
    const startDate = period?.start_date
      ? new Date(period.start_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    const endDate = period?.end_date
      ? new Date(period.end_date).toISOString().split('T')[0]
      : new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0];

    const toMemberPayload = (row) => {
      const {
        appData,
        exists_in_system: _exists,
        track_period_label: _trackLabel,
        vendor_groups_periods_label: _vendorLabel,
        reference_maps: _referenceMaps,
        changes: _changes,
        already_enrolled: _alreadyEnrolled,
        user_id: _userId,
        ...userFields
      } = row;
      return {
        ...userFields,
        // Preserve CSV identity ids when opted in (`id`, `user_groups_periods_id`).
        student_information: appData?.student_information || null
      };
    };

    setMemberActionMessage(
      overwriteExisting && overwriteRows.length > 0 && usersToCreate.length === 0
        ? 'Updating existing users…'
        : overwriteExisting && overwriteRows.length > 0
          ? 'Importing new users and updating existing…'
          : 'Importing users…'
    );
    setAddingUser(true);
    try {
      const newMembers = (usersToCreate || []).map(toMemberPayload);
      const overwriteMembers = (overwriteRows || []).map(toMemberPayload);
      const members =
        overwriteExisting && overwriteMembers.length > 0
          ? [...newMembers, ...overwriteMembers]
          : newMembers;

      let importedCount = 0;
      let updatedCount = 0;
      let serverSkipped = [];

      try {
        if (members.length > 0) {
          const result = await bulkImportMembersInBatches({
            token,
            groupsPeriodsId: selectedRelatedPeriodId,
            startDate,
            endDate,
            ignoreDuplicates,
            overwriteExisting: overwriteExisting && overwriteMembers.length > 0,
            overwriteColumns,
            members
          });
          importedCount = result.importedCount;
          updatedCount = result.updatedCount || 0;
          serverSkipped = result.skipped || [];
        }
      } catch (err) {
        if (err.importedBeforeFailure > 0) {
          await loadMembersForPeriod(selectedRelatedGroup.id, selectedRelatedPeriodId);
        }
        const batchNote =
          err.batchCount > 1
            ? ` Batch ${(err.batchIndex ?? 0) + 1} of ${err.batchCount} failed.`
            : '';
        const partialNote =
          err.importedBeforeFailure > 0
            ? ` ${err.importedBeforeFailure} user${err.importedBeforeFailure === 1 ? '' : 's'} were processed before the error.`
            : '';
        throw new Error(`${err.message || 'Bulk import failed.'}${batchNote}${partialNote}`);
      }

      const totalSkipped = skippedDuplicates.length + serverSkipped.length;
      const totalDone = importedCount + updatedCount;

      if (totalDone > 0) {
        await loadMembersForPeriod(selectedRelatedGroup.id, selectedRelatedPeriodId);
      }

      if (totalDone === 0) {
        throw new Error(
          totalSkipped > 0
            ? 'No users were imported. All rows were duplicates or already up to date.'
            : 'No users were imported.'
        );
      }

      setShowAddUserModal(false);
    } finally {
      setAddingUser(false);
    }
  };

  const openAddUserModal = async () => {
    setShowAddUserModal(true);
    setLoadingUsersForAdd(true);
    try {
      const response = await fetch('/api/users', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const memberIds = new Set((groupMembers || []).map(m => String(m.id)));
        setExistingUserEmails(
          data.data.map((u) => u.email).filter(Boolean)
        );
        setAvailableUsersList(data.data.filter(u => !memberIds.has(String(u.id))));
      } else {
        setExistingUserEmails([]);
        setAvailableUsersList([]);
      }
    } catch (error) {
      console.error('Failed to load users for add:', error);
      setExistingUserEmails([]);
      setAvailableUsersList([]);
    } finally {
      setLoadingUsersForAdd(false);
    }
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    setCreatingSchool(true);
    try {
      // Create the group with group_type set to 'school'
      const response = await fetchWithAuth('/api/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: createSchoolFormData.name,
          description: createSchoolFormData.description,
          groupPeriodDescription: formatOrganizationGroupPeriodDescription(
            createSchoolFormData.name
          ),
          users: [],
          group_type: groupType
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reload schools
        await loadSchools();
        setShowCreateSchoolModal(false);
        resetCreateOrganizationForm();
      } else {
        alert(data.message || 'Failed to create school');
      }
    } catch (error) {
      console.error('Error creating school:', error);
      alert('Failed to create school. Please try again.');
    } finally {
      setCreatingSchool(false);
    }
  };

  const handleCreateGroupPeriodValidity = useCallback(({ periodReady }) => {
    setCreateGroupPeriodReady(Boolean(periodReady));
  }, []);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!selectedSchoolMostRecentGpId && !createGroupLink.parentGpId && !createGroupLink.anchorGroup) {
      alert('No organization period selected');
      return;
    }
    const batchActive = createGroupBatchMode && createGroupAllowsBatch;
    if (!batchActive) {
      const ownerBlockReason = ownerTeamActionBlockedReason({
        platformOwnerViewer,
        group: createGroupLink.anchorGroup,
        proposedGroupName: createGroupFormData.name,
        parentGroupId: createGroupLink.anchorGroup?.id || selectedSchool?.id,
        allGroups: relatedGroupsRaw,
        orgGroupId: selectedSchool?.id,
        orgGpId: selectedSchoolMostRecentGpId
      });
      if (ownerBlockReason) {
        alert(ownerTeamActionBlockedMessage(ownerBlockReason));
        return;
      }
    }
    setCreatingGroup(true);
    setCreateGroupLoadingMessage('Preparing…');
    let periodWasCreated = false;
    try {
      let periodId = createGroupFormData.periodId;
      let groupPeriodTitle = createGroupFormData.groupPeriodTitle;
      let groupPeriodDescription = createGroupFormData.groupPeriodDescription;
      if (!createGroupFormData.periodId) {
        setCreateGroupLoadingMessage('Creating period…');
      }
      try {
        const resolved = await createGroupFormFieldsRef.current?.resolvePeriodIdForSubmit?.();
        if (resolved?.periodId) {
          periodId = resolved.periodId;
          periodWasCreated = Boolean(resolved.periodWasCreated);
          if (resolved.groupPeriodTitle) {
            groupPeriodTitle = resolved.groupPeriodTitle;
          }
          if (resolved.groupPeriodDescription) {
            groupPeriodDescription = resolved.groupPeriodDescription;
          }
        }
      } catch (periodErr) {
        alert(periodErr.message || 'Could not create period');
        return;
      }
      if (!periodId) {
        alert('Select an existing period or complete the new period fields.');
        return;
      }

      if (createGroupLink.anchorGroup && createGroupLink.addMode === 'team_period') {
        const anchor = createGroupLink.anchorGroup;
        const periodDef = periods.find((p) => String(p.id) === String(periodId));
        const periodContext = buildCreateGroupPeriodDescriptionContext({
          ...createGroupLink,
          addMode: 'team_period'
        });
        const gpTitle =
          String(groupPeriodTitle || '').trim() ||
          suggestGroupPeriodTitle(anchor.name, periodDef);
        const gpDescription =
          String(groupPeriodDescription || '').trim() ||
          buildGroupPeriodComboSuggests(
            anchor.name,
            periodDef,
            selectedSchool?.name,
            periodContext
          ).groupPeriodDescription;
        const periodName = periodDef?.title || periodDef?.code || 'the new period';
        setCreateGroupLoadingMessage('Adding period to team…');
        const gpRes = await fetch('/api/groups-periods', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_id: anchor.id,
            period_id: periodId,
            title: gpTitle,
            description: gpDescription || null
          })
        });
        const gpData = await gpRes.json();
        if (!gpData.success) {
          alert(gpData.message || 'Failed to add period to this team');
          return;
        }

        const newChildGpId = gpData.data?.id;
        const hierarchyParentGpId = anchor.parent_groups_periods_id || null;

        if (newChildGpId && hierarchyParentGpId) {
          setCreateGroupLoadingMessage('Linking to parent period…');
          const relationshipResponse = await fetch('/api/relationships', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              parent_table: 'groups_periods',
              parent_id: hierarchyParentGpId,
              child_table: 'groups_periods',
              child_id: newChildGpId,
              relationship_type: 'team_nested'
            })
          });
          const relationshipData = await relationshipResponse.json();
          if (!relationshipData.success) {
            alert(
              `Period was added on ${anchor.name}, but it could not be linked in the team tree: ${
                relationshipData.message || 'Unknown error'
              }`
            );
            return;
          }
        } else if (newChildGpId && !hierarchyParentGpId) {
          console.warn(
            'Added groups_periods without hierarchy parent; team may not appear under its parent in the map.',
            anchor.id
          );
        }

        if (newChildGpId) {
          const templateResult = await applyCreateGroupRoleTemplate(newChildGpId);
          warnRoleTemplateApplyFailure(templateResult);
        }

        setCreateGroupLoadingMessage('Updating groups…');
        await loadRelatedGroupsForParentGp(selectedSchoolMostRecentGpId);
        setShowCreateGroupModal(false);
        resetCreateGroupForm();
        setCornerNotice({
          isOpen: true,
          title: 'Success',
          text: periodWasCreated
            ? `Added ${periodName} to ${anchor.name}. A new school-year period was created for your organization.`
            : `Added ${periodName} to ${anchor.name}.`,
          position: 'bottom-left',
          containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          textColor: '#14532d',
          showOverlay: false,
          dismissOnClick: true,
          autoClose: true,
          viewTime: 5500
        });
        return;
      }

      const linkParentGpId = resolveCreateGroupParentGpId();
      if (!linkParentGpId) {
        alert(
          createGroupLink.anchorGroup
            ? 'Select which period on the parent team to attach under.'
            : 'No organization period selected.'
        );
        return;
      }

      const periodDef =
        periods.find((p) => String(p.id) === String(periodId)) || nestedParentPeriod;
      const descContext = buildCreateGroupPeriodDescriptionContext(createGroupLink);
      const namesToCreate = batchActive
        ? createGroupBatchNames
        : [createGroupFormData.name?.trim()].filter(Boolean);

      if (namesToCreate.length === 0) {
        alert(batchActive ? 'Enter at least one group name.' : 'Enter a group name.');
        return;
      }

      for (const proposedName of namesToCreate) {
        const nameBlockReason = ownerTeamActionBlockedReason({
          platformOwnerViewer,
          group: createGroupLink.anchorGroup,
          proposedGroupName: proposedName,
          parentGroupId: createGroupLink.anchorGroup?.id || selectedSchool?.id,
          allGroups: relatedGroupsRaw,
          orgGroupId: selectedSchool?.id,
          orgGpId: selectedSchoolMostRecentGpId
        });
        if (nameBlockReason) {
          alert(ownerTeamActionBlockedMessage(nameBlockReason));
          return;
        }
      }

      const createdNames = [];
      const failures = [];

      for (let i = 0; i < namesToCreate.length; i += 1) {
        const groupName = namesToCreate[i];
        setCreateGroupLoadingMessage(
          namesToCreate.length > 1
            ? `Creating ${i + 1} of ${namesToCreate.length}: ${groupName}…`
            : 'Creating group…'
        );

        const description = batchActive
          ? suggestGroupDescription(groupName, selectedSchool?.name, descContext)
          : createGroupFormData.description;
        const combo = batchActive
          ? buildGroupPeriodComboSuggests(
              groupName,
              periodDef,
              selectedSchool?.name,
              descContext
            )
          : {
              groupPeriodTitle,
              groupPeriodDescription
            };
        const nextGpTitle =
          String(combo.groupPeriodTitle || '').trim() ||
          suggestGroupPeriodTitle(groupName, periodDef);
        const nextGpDescription = String(combo.groupPeriodDescription || '').trim() || null;

        try {
          const response = await fetchWithAuth('/api/groups', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: groupName,
              description,
              periodId,
              groupPeriodTitle: nextGpTitle,
              groupPeriodDescription: nextGpDescription,
              organization_group_id: selectedSchool?.id,
              users: [],
              group_type: 'team',
              membership_kind: createGroupMembershipKind
            })
          });

          const data = await response.json();

          if (!data.success) {
            failures.push({ name: groupName, message: data.message || 'Failed to create group' });
            if (!batchActive) {
              alert(data.message || 'Failed to create group');
              return;
            }
            continue;
          }

          const newGroupsPeriodsId = data.data?.groups_periods_id;
          if (!newGroupsPeriodsId) {
            failures.push({
              name: groupName,
              message: 'Group created but groups_periods id was not returned'
            });
            if (!batchActive) {
              alert('Group created but groups_periods id was not returned');
              return;
            }
            continue;
          }

          const relationshipResponse = await fetch('/api/relationships', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              parent_table: 'groups_periods',
              parent_id: linkParentGpId,
              child_table: 'groups_periods',
              child_id: newGroupsPeriodsId,
              relationship_type: 'team_nested'
            })
          });

          const relationshipData = await relationshipResponse.json();

          if (!relationshipData.success) {
            failures.push({
              name: groupName,
              message:
                'Group created but failed to link to organization: ' +
                (relationshipData.message || 'Unknown error')
            });
            if (!batchActive) {
              alert(
                'Group created but failed to link to organization: ' +
                  (relationshipData.message || 'Unknown error')
              );
              return;
            }
            continue;
          }

          const templateResult = await applyCreateGroupRoleTemplate(newGroupsPeriodsId);
          warnRoleTemplateApplyFailure(templateResult);
          createdNames.push(groupName);
        } catch (createErr) {
          console.error('Error creating group:', createErr);
          failures.push({
            name: groupName,
            message: createErr.message || 'Failed to create group'
          });
          if (!batchActive) {
            alert('Failed to create group. Please try again.');
            return;
          }
        }
      }

      if (createdNames.length > 0) {
        setCreateGroupLoadingMessage('Updating groups…');
        await loadRelatedGroupsForParentGp(selectedSchoolMostRecentGpId);
        setShowCreateGroupModal(false);
        resetCreateGroupForm();
        setCreatingGroup(false);

        let noticeText;
        if (createdNames.length === 1 && failures.length === 0) {
          noticeText = periodWasCreated
            ? `${createdNames[0]} and its time period were created.`
            : `${createdNames[0]} was created.`;
        } else if (failures.length === 0) {
          noticeText = periodWasCreated
            ? `Created ${createdNames.length} groups. A new school-year period was created for your organization.`
            : `Created ${createdNames.length} groups.`;
        } else {
          noticeText = `Created ${createdNames.length} of ${namesToCreate.length} groups. Failed: ${failures
            .map((f) => f.name)
            .join(', ')}.`;
        }

        setCornerNotice({
          isOpen: true,
          title: failures.length > 0 ? 'Partially complete' : 'Success',
          text: noticeText,
          position: 'bottom-left',
          containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          textColor: '#14532d',
          showOverlay: false,
          dismissOnClick: true,
          autoClose: true,
          viewTime: failures.length > 0 ? 8000 : 5500
        });
        return;
      }

      if (failures.length > 0) {
        alert(
          failures.length === 1
            ? failures[0].message
            : `Could not create any groups.\n${failures
                .map((f) => `${f.name}: ${f.message}`)
                .join('\n')}`
        );
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group. Please try again.');
    } finally {
      setCreatingGroup(false);
    }
  };

  const activeRelatedPeriod = selectedRelatedGroup?.periods?.find(
    (p) => String(p.id) === String(selectedRelatedPeriodId)
  );
  const activeRelatedPeriodLabel =
    activeRelatedPeriod?.period_title || activeRelatedPeriod?.title || activeRelatedPeriod?.period_code || '';
  const membersReturnUrl = selectedRelatedGroup
    ? `/groups?returnTo=members&schoolId=${selectedSchool?.id}&groupId=${selectedRelatedGroup?.id}&periodId=${selectedRelatedPeriodId}`
    : '/groups';

  const activeGroup = selectedRelatedGroup || groupsBrowseFocus?.group;

  const pageTitle = showOrgPicker
    ? MY_GROUPS_TITLE
    : activeGroup
      ? activeGroup.name || MY_GROUPS_TITLE
      : selectedSchool?.name || MY_GROUPS_TITLE;

  const groupSubtitle = useMemo(
    () => (activeGroup ? getGroupPageSubtitle(activeGroup) : ''),
    [activeGroup]
  );

  const pageSubtitle = useMemo(() => {
    if (activeGroup) return '';
    if (showOrgPicker) {
      return `Select an ${entityLabel.toLowerCase()} or switch context to work within one.`;
    }
    if (!selectedSchool) {
      return 'Groups in your current organization.';
    }
    return selectedSchool.description || 'Groups in this organization.';
  }, [activeGroup, showOrgPicker, selectedSchool, entityLabel]);

  const showBreadcrumbs =
    Boolean(selectedRelatedGroup) ||
    Boolean(groupsBrowseFocus) ||
    (Boolean(selectedSchool) && !showOrgPicker && !scopeParentId);
  const focusGroupForBreadcrumb = (group) => {
    setGroupsBrowseFocus({ group });
    setMapCameraNonce((n) => n + 1);
    setSelectedRelatedGroup(null);
    setSelectedRelatedPeriodId(null);
    setGroupMembers([]);
    setDerivedGuardiansData(null);
    setDerivedGuardiansError(null);
    setRelatedListSearch('');
  };

  const breadcrumbItems = showBreadcrumbs
    ? buildGroupsPageBreadcrumbs({
        selectedSchool: showOrgPicker ? null : selectedSchool,
        relatedGroups,
        groupsBrowseFocus,
        selectedRelatedGroup,
        selectedRelatedPeriodId,
        showAllOrganizationsLink: canBrowseAll && !scopeParentId && !showOrgPicker && allSchools.length > 1,
        allOrganizationsLabel: `All ${entityLabelPlural.toLowerCase()}`,
        onGoToOrganizations: goToOrganizations,
        onGoToOrgTop: goToOrgGroupsTop,
        onFocusGroup: focusGroupForBreadcrumb
      })
    : [];

  return (
    <div className="groups-page">
      <LoadingOverlay
        isVisible={isLoading || !orgContextReady}
        message={`Loading ${entityLabelPlural.toLowerCase()}…`}
      />
      <LoadingOverlay
        isVisible={creatingGroup}
        message={createGroupLoadingMessage}
        style={{ zIndex: 10060 }}
      />
      <LoadingOverlay
        isVisible={addingUser}
        message={memberActionMessage}
        style={{ zIndex: 10060 }}
      />
      {cornerNotice ? (
        <CornerNotice
          {...cornerNotice}
          onClose={() => setCornerNotice(null)}
        />
      ) : null}

      <div className="groups-shell">
        <header className="groups-top">
          <div>
            <h1 className="groups-title">{pageTitle}</h1>
            {(groupSubtitle || pageSubtitle) && (
              <p className="groups-subtitle">{groupSubtitle || pageSubtitle}</p>
            )}
          </div>
          {showBreadcrumbs && <GroupsBreadcrumbs items={breadcrumbItems} />}
        </header>

        <div
          className={`groups-view ${
            showOrgPicker ? 'groups-view--visible' : 'groups-view--hidden-left'
          }`}
        >
          <div
            className={
              showAddOrganizationEmpty ? 'groups-org-picker-body groups-org-picker-body--empty' : 'groups-org-picker-body'
            }
          >
            <GroupsRowList
              eyebrow={`All ${entityLabelPlural.toLowerCase()}`}
              count={filteredSchools.length}
              searchListSize={allSchools.length}
              hint="Open an organization to view its groups."
              search={orgListSearch}
              onSearch={setOrgListSearch}
              searchPlaceholder={`Search ${entityLabelPlural.toLowerCase()}…`}
              loading={isLoading || !orgContextReady}
              emptyMessage={
                allSchools.length === 0
                  ? `No ${entityLabelPlural.toLowerCase()} found.`
                  : 'No matching organizations.'
              }
              emptyState={
                showAddOrganizationEmpty ? (
                  <GroupsAddOrganizationEmptyState
                    onClick={openCreateOrganizationModal}
                    label={`Add ${entityLabel.toLowerCase()}`}
                  />
                ) : null
              }
              rows={filteredSchools.map((group) => ({
                id: group.id,
                name: group.name,
                groupType: group.group_type || 'organization',
                displayLabel: group.display_label || null,
                logoUrl: group.logo_url,
                logoUploadable:
                  canCreateOrganizationsHere &&
                  String(group.group_type || '').toLowerCase() !== 'system',
                onLogoUploaded: (url) => handleOrgLogoUploaded(group.id, url),
                onLogoUploadError: handleOrgLogoUploadError,
                metaParts: group.description ? [group.description] : [],
                onClick: () => handleSchoolClick(group)
              }))}
              useLogos
            />
          </div>
          {canCreateOrganizationsHere && showOrgPicker && !showAddOrganizationEmpty ? (
            <GroupsCreateFab
              onClick={openCreateOrganizationModal}
              ariaLabel={`Create ${entityLabel.toLowerCase()}`}
            />
          ) : null}
        </div>

        <div
          className={`groups-view ${
            selectedSchool && !selectedRelatedGroup && !showOrgPicker
              ? 'groups-view--visible'
              : selectedRelatedGroup
                ? 'groups-view--hidden-left'
                : 'groups-view--hidden-right'
          }`}
        >
          {selectedSchool && !showOrgPicker && (
            <>
              <GroupsOrgGroupsPanel
                organization={selectedSchool}
                orgGpId={selectedSchoolMostRecentGpId}
                allGroups={relatedGroups}
                browseFocus={groupsBrowseFocus}
                focusGroupId={groupsBrowseFocus?.group?.id || null}
                cameraNonce={mapCameraNonce}
                searchListSize={relatedGroups.length}
                loading={loadingRelated}
                search={relatedListSearch}
                onSearch={setRelatedListSearch}
                searchPlaceholder="Search groups…"
                emptyMessage={
                  relatedGroupsRaw.length === 0
                    ? 'No related groups found.'
                    : 'No groups match your search or access.'
                }
                onSelectGroup={handleSelectGroupNode}
                onSelectPeriod={handleSelectPeriodNode}
                canAddGroup={canCreateForCurrentBrowseScope}
                onAddGroup={openCreateGroupModal}
                platformOwnerViewer={platformOwnerViewer}
                userId={user?.id}
                availableUgp={availableUgp}
                userGroupsPeriods={userGroupsPeriods}
                currentRole={currentRole}
                showDerivedGuardiansNode={showDerivedGuardiansNode}
                derivedParentGpIds={derivedParentGpIds}
              />
            </>
          )}
        </div>

        <div
          className={`groups-view ${selectedRelatedGroup ? 'groups-view--visible' : 'groups-view--hidden-right'}`}
        >
          {selectedRelatedGroup && isDerivedGuardiansGroup(selectedRelatedGroup) && (
            <GroupsDerivedGuardiansPanel
              data={derivedGuardiansData}
              loading={loadingDerivedGuardians}
              error={derivedGuardiansError}
              canOpenMemberProfiles={canOpenMemberProfilesForSelectedGroup}
              canViewMemberDetails={canViewMemberDetailsForSelectedGroup}
              returnUrlBase={membersReturnUrl}
            />
          )}
          {selectedRelatedGroup && !isDerivedGuardiansGroup(selectedRelatedGroup) && (
            <GroupsMembersPanel
              periods={selectedRelatedGroup.periods || []}
              periodValue={selectedRelatedPeriodId}
              onPeriodChange={handleMemberPeriodChange}
              periodDisabled={loadingMembers}
              canAddMembers={canAddMembersForSelectedGroup}
              canOpenMemberProfiles={canOpenMemberProfilesForSelectedGroup}
              canViewGroupMembers={canViewGroupMembersForSelectedGroup}
              canViewMemberDetails={canViewMemberDetailsForSelectedGroup}
              canViewMemberAccess={canViewMemberAccessForSelectedGroup}
              currentUserId={user?.id}
              onAddMember={openAddUserModal}
              members={groupMembers}
              loading={loadingMembers}
              emptyMessage="No members in this group for the selected period."
              returnUrlBase={membersReturnUrl}
            />
          )}
        </div>

        <GroupsAddMemberModal
          open={canAddMembersForSelectedGroup && showAddUserModal}
          onClose={() => !addingUser && setShowAddUserModal(false)}
          submitting={addingUser}
          groupLabel={selectedRelatedGroup?.name || 'Group'}
          periodLabel={activeRelatedPeriodLabel}
          users={availableUsersList}
          existingEmails={existingUserEmails}
          groupsPeriodsId={selectedRelatedPeriodId}
          authToken={token}
          loadingUsers={loadingUsersForAdd}
          onAddExisting={handleAddExistingMember}
          onCreateNew={handleCreateAndAddMember}
          onBulkCreateNew={handleBulkCreateAndAddMembers}
        />

      <ProfileFormSheet
        open={canCreateForCurrentBrowseScope && showCreateGroupModal}
        onClose={() => {
          if (creatingGroup) return;
          setShowCreateGroupModal(false);
          resetCreateGroupForm();
        }}
        closeDisabled={creatingGroup}
        title={
          createGroupLink.anchorGroup ? `Add under — ${createGroupLink.anchorGroup.name}` : 'Create new group'
        }
        titleId="create-group-title"
        subtext={createGroupModalSubtext}
        panelClassName="profile-sheet-panel--form-wide"
        footer={
          <button
            type="submit"
            form="create-group-form"
            className="profile-sheet-btn profile-sheet-btn--primary"
            disabled={creatingGroup || !createGroupSubmitReady}
          >
            {createGroupPrimaryLabel}
          </button>
        }
      >
        <form id="create-group-form" onSubmit={handleCreateGroup} className="groups-create-form">
          {createGroupLink.anchorGroup ? (
            <GroupsTeamAddContextFields
              anchorGroup={createGroupLink.anchorGroup}
              addMode={createGroupLink.addMode}
              onAddModeChange={(mode) => {
                if (mode === createGroupLink.addMode) return;
                const prev = createGroupLink;
                const teamPeriods = prev.anchorGroup?.periods || [];
                let parentAttachGpId = prev.parentAttachGpId
                  ? String(prev.parentAttachGpId)
                  : null;
                if (mode === 'nested_team' && !parentAttachGpId) {
                  const selectedOnTeam = teamPeriods.some(
                    (gp) => String(gp.id) === String(selectedRelatedPeriodId)
                  )
                    ? String(selectedRelatedPeriodId)
                    : null;
                  parentAttachGpId =
                    selectedOnTeam ||
                    (teamPeriods[0] ? String(teamPeriods[0].id) : null);
                }
                const next = { ...prev, addMode: mode, parentAttachGpId };
                setCreateGroupLink(next);

                if (mode === 'nested_team') {
                  setNestedTeamPeriodChoice('parent');
                  applyNestedTeamPeriodToForm(next);
                  return;
                }

                setCreateGroupBatchMode(false);
                setCreateGroupBatchNamesText('');
                setCreateGroupFormData((formPrev) => {
                  const usedOnTeam = periodDefinitionIdsOnTeam(prev.anchorGroup);
                  const clearedUsed =
                    formPrev.periodId && usedOnTeam.has(String(formPrev.periodId))
                      ? { periodId: '', groupPeriodTitle: '', groupPeriodDescription: '' }
                      : null;
                  const periodDef = periods.find(
                    (p) => String(p.id) === String(clearedUsed ? '' : formPrev.periodId)
                  );
                  if (!periodDef) {
                    return clearedUsed ? { ...formPrev, ...clearedUsed } : formPrev;
                  }
                  const descContext = buildCreateGroupPeriodDescriptionContext(next, periodDef);
                  if (!descContext) {
                    return clearedUsed ? { ...formPrev, ...clearedUsed } : formPrev;
                  }
                  return {
                    ...formPrev,
                    ...(clearedUsed || {}),
                    ...buildGroupPeriodComboSuggests(
                      prev.anchorGroup?.name,
                      periodDef,
                      selectedSchool?.name,
                      descContext
                    )
                  };
                });
              }}
              parentAttachGpId={createGroupLink.parentAttachGpId || ''}
              onParentAttachChange={(id) => {
                const nextAttach = id || null;
                if (String(nextAttach || '') === String(createGroupLink.parentAttachGpId || '')) {
                  return;
                }
                const next = {
                  ...createGroupLink,
                  parentAttachGpId: nextAttach,
                  addMode: 'nested_team'
                };
                setCreateGroupLink(next);
                if (nextAttach && nestedTeamPeriodChoice === 'parent') {
                  applyNestedTeamPeriodToForm(next);
                }
              }}
              disabled={creatingGroup}
            />
          ) : null}

          {(() => {
            const oneMultipleSlider = createGroupAllowsBatch ? (
              <div
                className="groups-create-count-slider"
                role="tablist"
                aria-label="Create one or multiple groups"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!createGroupBatchMode}
                  className={`groups-create-count-slider-btn${!createGroupBatchMode ? ' is-active' : ''}`}
                  onClick={() => setCreateGroupBatchMode(false)}
                  disabled={creatingGroup}
                >
                  One
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={createGroupBatchMode}
                  className={`groups-create-count-slider-btn${createGroupBatchMode ? ' is-active' : ''}`}
                  onClick={() => setCreateGroupBatchMode(true)}
                  disabled={creatingGroup}
                >
                  Multiple
                </button>
              </div>
            ) : null;

            const createFields = (
              <>
                <GroupsRoleTemplateField
                  value={createGroupRoleTemplateId}
                  onChange={setCreateGroupRoleTemplateId}
                  disabled={creatingGroup}
                />
                {createGroupBatchMode && createGroupAllowsBatch ? (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-label" htmlFor="create-group-batch-names">
                      Names of groups *
                    </label>
                    <p className="profile-sheet-hint">
                      One name per line. Shared settings below apply to every group; About and
                      group–period text are filled from each name.
                    </p>
                    <textarea
                      id="create-group-batch-names"
                      className="profile-sheet-textarea"
                      value={createGroupBatchNamesText}
                      onChange={(e) => setCreateGroupBatchNamesText(e.target.value)}
                      placeholder={'Grade 9\nGrade 10\nGrade 11'}
                      rows={4}
                      required
                      disabled={creatingGroup}
                    />
                    {createGroupBatchNames.length > 0 ? (
                      <p className="profile-sheet-muted">
                        {createGroupBatchNames.length} group
                        {createGroupBatchNames.length === 1 ? '' : 's'} ready
                      </p>
                    ) : null}
                    {createGroupBatchPreview ? (
                      <div className="groups-batch-create-preview" aria-live="polite">
                        <p className="profile-sheet-label">
                          Preview (from “{createGroupBatchPreview.sampleName}”)
                        </p>
                        <p className="profile-sheet-muted">
                          About: {createGroupBatchPreview.about}
                        </p>
                        {createGroupBatchPreview.groupPeriodTitle ? (
                          <p className="profile-sheet-muted">
                            Title: {createGroupBatchPreview.groupPeriodTitle}
                          </p>
                        ) : null}
                        {createGroupBatchPreview.groupPeriodDescription ? (
                          <p className="profile-sheet-muted">
                            Description: {createGroupBatchPreview.groupPeriodDescription}
                          </p>
                        ) : null}
                        {createGroupBatchPreview.total > 1 ? (
                          <p className="profile-sheet-hint">
                            Same pattern for the other {createGroupBatchPreview.total - 1} name
                            {createGroupBatchPreview.total === 2 ? '' : 's'}.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <GroupsCreateEntityFormFields
                  ref={createGroupFormFieldsRef}
                  entityKind="group"
                  nameLabel="Group name"
                  formData={createGroupFormData}
                  setFormData={setCreateGroupFormData}
                  periods={createGroupPeriodOptions}
                  organizationGroupId={selectedSchool?.id}
                  organizationName={selectedSchool?.name}
                  onPeriodCreated={handlePeriodCreated}
                  onPeriodValidityChange={handleCreateGroupPeriodValidity}
                  allowPermanentPeriodCreate={allowPermanentPeriodCreate}
                  allowNewPeriodCreate={allowNewPeriodCreate}
                  disabled={creatingGroup}
                  hideGroupIdentity={
                    (Boolean(createGroupLink.anchorGroup) &&
                      createGroupLink.addMode === 'team_period') ||
                    (createGroupBatchMode && createGroupAllowsBatch)
                  }
                  hideGroupPeriodTextFields={createGroupBatchMode && createGroupAllowsBatch}
                  nestedTeamCreate={
                    Boolean(createGroupLink.anchorGroup) &&
                    createGroupLink.addMode === 'nested_team'
                  }
                  suggestedExistingPeriodId={nestedTeamSuggestedPeriodId}
                  nestedParentPeriod={nestedParentPeriod}
                  nestedParentTeamName={createGroupLink.anchorGroup?.name || ''}
                  nestedPeriodChoice={nestedTeamPeriodChoice}
                  onNestedPeriodChoiceChange={setNestedTeamPeriodChoice}
                  periodDescriptionContext={buildCreateGroupPeriodDescriptionContext(
                    createGroupLink
                  )}
                  scopeSubjectName={
                    createGroupLink.anchorGroup && createGroupLink.addMode === 'team_period'
                      ? createGroupLink.anchorGroup.name
                      : createGroupBatchMode && createGroupAllowsBatch
                        ? createGroupBatchNames[0] || 'Group'
                        : createGroupFormData.name
                  }
                />
              </>
            );

            if (createGroupAllowsBatch) {
              return (
                <GroupsMembershipKindField
                  value={createGroupMembershipKind}
                  onChange={setCreateGroupMembershipKind}
                  disabled={creatingGroup}
                  trailing={oneMultipleSlider}
                >
                  {createFields}
                </GroupsMembershipKindField>
              );
            }

            return (
              <>
                {oneMultipleSlider ? (
                  <div className="groups-create-toolbar">{oneMultipleSlider}</div>
                ) : null}
                {createFields}
              </>
            );
          })()}
        </form>
      </ProfileFormSheet>

      <ProfileFormSheet
        open={canCreateOrganizationsHere && showCreateSchoolModal}
        onClose={() => {
          if (creatingSchool) return;
          setShowCreateSchoolModal(false);
          resetCreateOrganizationForm();
        }}
        closeDisabled={creatingSchool}
        title={`Create new ${entityLabel.toLowerCase()}`}
        titleId="create-organization-title"
        subtext={`Creates the ${entityLabel.toLowerCase()} with its own Permanent period.`}
        footer={
          <button
            type="submit"
            form="create-organization-form"
            className="profile-sheet-btn profile-sheet-btn--primary"
            disabled={creatingSchool || !createSchoolFormData.name}
          >
            {creatingSchool ? 'Creating…' : 'Create'}
          </button>
        }
      >
        <form id="create-organization-form" onSubmit={handleCreateSchool}>
          <GroupsCreateEntityFormFields
            entityKind={groupType === 'school' ? 'school' : 'organization'}
            nameLabel={`${entityLabel} name`}
            formData={createSchoolFormData}
            setFormData={setCreateSchoolFormData}
            periods={periods}
            disabled={creatingSchool}
            lockPeriodToDefault
          />
        </form>
      </ProfileFormSheet>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: '15px',
  },
  description: {
    fontSize: '1rem',
    color: '#6b7280',
    lineHeight: '1.6',
    marginBottom: '25px',
  },
  view: {
    position: 'relative',
    transition: 'transform 0.35s ease, opacity 0.35s ease',
  },
  viewVisible: {
    transform: 'translateX(0)',
    opacity: 1,
  },
  viewHiddenLeft: {
    transform: 'translateX(-40px)',
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    left: 0,
    right: 0,
  },
  viewHiddenRight: {
    transform: 'translateX(40px)',
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    left: 0,
    right: 0,
  },
  groupsList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '1.1rem',
  },
  groupCard: {
    background: '#f8f9fa',
    border: '1px solid #e1e5e9',
    borderRadius: '12px',
    padding: '25px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    cursor: 'pointer',
  },
  groupCardHover: {
    background: '#fff',
    border: '1px solid #cbd5e1',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transform: 'translateY(-2px)',
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  groupName: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#374151',
    margin: 0,
  },
  groupType: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: '#e0e7ff',
    color: '#3730a3',
    textTransform: 'uppercase',
  },
  groupDescription: {
    color: '#6b7280',
    marginBottom: '15px',
    lineHeight: '1.5',
  },
  periodsSection: {
    marginBottom: '15px',
    fontSize: '0.9rem',
  },
  periodTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  periodTag: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  addPeriodButton: {
    width: '24px',
    height: '24px',
    padding: 0,
    fontSize: '1.2rem',
    background: '#f8fafc',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderColor: '#e2e8f0',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: '300',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
  },
  backButton: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    color: '#374151',
    borderRadius: '999px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  detailTitle: {
    margin: 0,
    fontSize: '1.4rem',
    color: '#374151',
  },
  detailSubtitle: {
    color: '#6b7280',
    marginBottom: '20px',
  },
  loadingRelated: {
    textAlign: 'center',
    color: '#6b7280',
    padding: '20px',
  },
  spinner: {
    width: '28px',
    height: '28px',
    margin: '0 auto',
    border: '3px solid #e5e7eb',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  periodSelector: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
  },
  periodSelectorLabel: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#374151',
  },
  periodSelect: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.9rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
    color: '#374151',
  },
  singlePeriod: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '0.9rem',
    color: '#6b7280',
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '1rem',
  },
  emptyStateText: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#9ca3af',
    fontSize: '1rem',
  },
  emptyStateInline: {
    color: '#9ca3af',
    fontSize: '1rem',
    flex: 1,
  },
  memberCount: {
    fontSize: '1.1rem',
    color: '#374151',
    marginBottom: 0,
    fontWeight: '600',
    margin: 0,
  },
  memberCountRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '12px',
  },
  addMemberButton: {
    width: '40px',
    height: '40px',
    padding: 0,
    fontSize: '1.75rem',
    background: '#f8fafc',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: '300',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addMemberButtonHover: {
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    color: '#475569',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-1px)',
  },
  membersList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  memberCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '20px',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  memberCardHover: {
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transform: 'translateY(-2px)',
  },
  memberCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
  },
  memberAvatarContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  memberAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    objectFit: 'cover',
    border: '2px solid #e5e7eb',
  },
  memberAvatarPlaceholder: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: '#f3f4f6',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '0.875rem',
    border: '1px solid #e5e7eb',
  },
  memberIndexBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    width: '18px',
    height: '18px',
    borderRadius: '9px',
    background: '#f3f4f6',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.65rem',
    fontWeight: '600',
    border: '1px solid #e5e7eb',
  },
  memberInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    minWidth: 0,
  },
  memberName: {
    fontSize: '1.05rem',
    fontWeight: '600',
    color: '#374151',
    lineHeight: '1.3',
  },
  memberEmail: {
    fontSize: '0.875rem',
    color: '#6b7280',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  addSchoolButtonContainer: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addSchoolButton: {
    width: '40px',
    height: '40px',
    padding: 0,
    fontSize: '1.75rem',
    background: '#f8fafc',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderColor: '#e2e8f0',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: '300',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  addSchoolButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    transition: 'transform 0.2s ease',
  },
  addSchoolButtonHover: {
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    borderColor: '#cbd5e1',
    color: '#475569',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-1px)',
  },
};

export default GroupsOrganizationView;
